// src/components/exercises/EnhancedCamera.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { detectSquats, detectJumpingJacks } from './exerciseDetection';
import { calculateSquatScore, calculateJJScore } from './exerciseScoring';
import { db } from '../../services/firebase/config';
import { doc, setDoc, getDoc, serverTimestamp, getDocs, collection } from 'firebase/firestore';
import './EnhancedCamera.css';

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="white" width="20" height="20">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const getCurrentWeekKey = () => {
  const now = new Date();
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const india = new Date(utc + (5.5 * 60 * 60 * 1000));
  const target = new Date(india.valueOf());
  const dayNr = (india.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  }
  const weekNumber = 1 + Math.ceil((firstThursday - target) / 604800000);
  return `${india.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`;
};

const EnhancedCamera = ({ exercise, onComplete, onBack, userId, clapUrl }) => {
  const language = localStorage.getItem('spocademy_language') || 'mr';

  const [exerciseCount, setExerciseCount] = useState(0);
  const [currentSet, setCurrentSet] = useState(1);
  const [isResting, setIsResting] = useState(false);
  const [restTime, setRestTime] = useState(0);
  const [status, setStatus] = useState('');
  const [showOutline, setShowOutline] = useState(true);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [totalRepsLabel, setTotalRepsLabel] = useState('');
  const [progressPct, setProgressPct] = useState(0);
  const [restProgressPct, setRestProgressPct] = useState(0);
  const [cameraStarted, setCameraStarted] = useState(false);

  const videoRef = useRef();
  const canvasRef = useRef();
  const poseRef = useRef();
  const cameraRef = useRef();
  const restTimerRef = useRef();
  const timeoutRef = useRef();
  const completionTimerRef = useRef();
  const audioCtxRef = useRef();
  const isDetectingRef = useRef(true);
  const isRestingRef = useRef(false);
  const smoothScoreRef = useRef(0);
  const barFillRef = useRef();

  const countingAudioMapRef = useRef({});
  const exerciseCompleteAudioRef = useRef(null);
  const restStartAudioRef = useRef(null);

  const hasGoneDown = useRef(false);
  const squatStartTime = useRef(null);
  const standingKneeAngle = useRef(null);
  const maxSquatDepth = useRef(null);
  const armsUp = useRef(false);
  const legsApart = useRef(false);
  const hasCompletedJump = useRef(false);
  const jumpStartTime = useRef(null);
  const currentCountRef = useRef(0);
  const currentSetRef = useRef(1);
  const totalRepsCompleted = useRef(0);
  const repsPerSetRef = useRef(0);
  const totalSetsRef = useRef(0);
  const restDurationRef = useRef(0);

  const repsPerSet = exercise?.reps || 10;
  const totalSets = exercise?.sets || 2;
  const restDuration = exercise?.restTime || 30;
  const exerciseType = exercise?.mediaPipeType || 'squats';
  const exerciseName = typeof exercise?.name === 'object'
    ? (exercise.name[language] || exercise.name.en)
    : (exercise?.name || 'Exercise');

  useEffect(() => {
    repsPerSetRef.current = repsPerSet;
    totalSetsRef.current = totalSets;
    restDurationRef.current = restDuration;
  }, [repsPerSet, totalSets, restDuration]);

  const t = language === 'mr' ? {
    setOf: 'पैकी', set: 'सेट', rest: 'विश्रांती', done: 'पूर्ण!',
    confirmClose: 'बंद केल्यास प्रगती जाईल. खात्री आहे का?',
    yes: 'होय', no: 'नाही', loading: 'कॅमेरा सुरू होत आहे...', ready: 'सुरू करा!'
  } : {
    setOf: 'of', set: 'Set', rest: 'Rest', done: 'Done!',
    confirmClose: 'Closing will lose your progress. Are you sure?',
    yes: 'Yes', no: 'No', loading: 'Starting camera...', ready: 'Start!'
  };

  useEffect(() => {
    const preloadAudio = async () => {
      try {
        const [exerciseSnap, restSnap] = await Promise.all([
          getDoc(doc(db, 'eventSounds', 'exercise_complete')),
          getDoc(doc(db, 'eventSounds', 'rest_start'))
        ]);
        if (exerciseSnap.exists() && exerciseSnap.data().audioURL) {
          const a = new Audio(exerciseSnap.data().audioURL);
          a.preload = 'auto';
          exerciseCompleteAudioRef.current = a;
        }
        if (restSnap.exists() && restSnap.data().audioURL) {
          const a = new Audio(restSnap.data().audioURL);
          a.preload = 'auto';
          restStartAudioRef.current = a;
        }
        const countingSnap = await getDocs(collection(db, 'countingAudio'));
        const map = {};
        countingSnap.docs.forEach(d => {
          const data = d.data();
          if (data.number && data.audioURL) {
            const a = new Audio(data.audioURL);
            a.preload = 'auto';
            map[data.number] = a;
          }
        });
        countingAudioMapRef.current = map;
      } catch (e) {}
    };
    preloadAudio();
  }, []);

  const initAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      try {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {}
    }
    if (audioCtxRef.current?.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
  }, []);

  const speakFallback = useCallback((text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = language === 'mr' ? 'hi-IN' : 'en-US';
    u.rate = 1.0;
    u.volume = 0.8;
    window.speechSynthesis.speak(u);
  }, [language]);

  const speakNumber = useCallback((n) => {
    const uploaded = countingAudioMapRef.current[n];
    if (uploaded) {
      try {
        uploaded.currentTime = 0;
        uploaded.play().catch(() => speakFallback(n.toString()));
        return;
      } catch (e) {}
    }
    speakFallback(n.toString());
  }, [speakFallback]);

  const speakMessage = useCallback((msg) => {
    speakFallback(msg);
  }, [speakFallback]);

  const playBeep = useCallback(() => {
    initAudio();
    if (!audioCtxRef.current) return;
    const ctx = audioCtxRef.current;
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(800, ctx.currentTime);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
    } catch (e) {}
  }, [initAudio]);

  const playWhistle = useCallback(() => {
    if (restStartAudioRef.current) {
      try {
        restStartAudioRef.current.currentTime = 0;
        restStartAudioRef.current.play().catch(() => {
          initAudio();
          if (!audioCtxRef.current) return;
          const ctx = audioCtxRef.current;
          try {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain); gain.connect(ctx.destination);
            osc.frequency.setValueAtTime(2000, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(2500, ctx.currentTime + 0.1);
            osc.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.2);
            gain.gain.setValueAtTime(0.4, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
            osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.3);
          } catch (e) {}
        });
        return;
      } catch (e) {}
    }
    initAudio();
    if (!audioCtxRef.current) return;
    const ctx = audioCtxRef.current;
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(2000, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(2500, ctx.currentTime + 0.1);
      osc.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.4, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  }, [initAudio]);

  const playClap = useCallback(() => {
    if (clapUrl) {
      try {
        const a = new Audio(clapUrl);
        a.play().catch(() => {});
        return;
      } catch (e) {}
    }
    if (exerciseCompleteAudioRef.current) {
      try {
        exerciseCompleteAudioRef.current.currentTime = 0;
        exerciseCompleteAudioRef.current.play().catch(() => {
          initAudio();
          if (!audioCtxRef.current) return;
          const ctx = audioCtxRef.current;
          const playBurst = (time) => {
            try {
              const bufferSize = Math.floor(ctx.sampleRate * 0.15);
              const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
              const data = buffer.getChannelData(0);
              for (let i = 0; i < bufferSize; i++) {
                data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.03));
              }
              const source = ctx.createBufferSource();
              source.buffer = buffer;
              const filter = ctx.createBiquadFilter();
              filter.type = 'bandpass'; filter.frequency.value = 1200; filter.Q.value = 0.8;
              const gain = ctx.createGain();
              gain.gain.setValueAtTime(0.6, time);
              gain.gain.exponentialRampToValueAtTime(0.01, time + 0.12);
              source.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
              source.start(time);
            } catch (e) {}
          };
          playBurst(ctx.currentTime);
          playBurst(ctx.currentTime + 0.12);
          playBurst(ctx.currentTime + 0.22);
        });
        return;
      } catch (e) {}
    }
    initAudio();
    if (!audioCtxRef.current) return;
    const ctx = audioCtxRef.current;
    const playBurst = (time) => {
      try {
        const bufferSize = Math.floor(ctx.sampleRate * 0.15);
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
          data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.03));
        }
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass'; filter.frequency.value = 1200; filter.Q.value = 0.8;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.6, time);
        gain.gain.exponentialRampToValueAtTime(0.01, time + 0.12);
        source.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
        source.start(time);
      } catch (e) {}
    };
    playBurst(ctx.currentTime);
    playBurst(ctx.currentTime + 0.12);
    playBurst(ctx.currentTime + 0.22);
  }, [initAudio, clapUrl]);

  const updateUserStatsBackground = useCallback(async (repsCount) => {
    if (!userId || !exerciseType) return;
    try {
      const statsRef = doc(db, 'fitnessUserStats', userId);
      const snap = await getDoc(statsRef);
      const existing = snap.exists() ? snap.data() : {};
      const field = exerciseType === 'squats' ? 'totalSquats' : 'totalJumpingJacks';
      const weekKey = getCurrentWeekKey();
      const weeklyStats = existing.weeklyStats || {};
      const currentWeek = weeklyStats[weekKey] || { squats: 0, jumpingJacks: 0 };
      const weekField = exerciseType === 'squats' ? 'squats' : 'jumpingJacks';
      currentWeek[weekField] = (currentWeek[weekField] || 0) + repsCount;
      weeklyStats[weekKey] = currentWeek;
      await setDoc(statsRef, {
        ...existing,
        [field]: (existing[field] || 0) + repsCount,
        weeklyStats,
        updatedAt: serverTimestamp()
      });
    } catch (e) {}
  }, [userId, exerciseType]);

  const cleanup = useCallback(() => {
    window.speechSynthesis?.cancel();
    clearInterval(restTimerRef.current);
    clearTimeout(timeoutRef.current);
    clearTimeout(completionTimerRef.current);
    isDetectingRef.current = false;
    try { poseRef.current?.close(); } catch (e) {}
    poseRef.current = null;
    try { cameraRef.current?.stop(); } catch (e) {}
    cameraRef.current = null;
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  }, []);

  const startRestTimer = useCallback(() => {
    isDetectingRef.current = false;
    isRestingRef.current = true;
    setIsResting(true);
    let timeLeft = restDurationRef.current;
    setRestTime(timeLeft);
    setRestProgressPct(0);
    speakMessage(language === 'mr' ? 'विश्रांती घे' : 'Take rest');
    playWhistle();

    restTimerRef.current = setInterval(() => {
      timeLeft--;
      setRestTime(timeLeft);
      setRestProgressPct(
        ((restDurationRef.current - timeLeft) / restDurationRef.current) * 100
      );
      if (timeLeft <= 5 && timeLeft > 0) speakNumber(timeLeft);
      if (timeLeft <= 0) {
        clearInterval(restTimerRef.current);
        isRestingRef.current = false;
        setIsResting(false);
        setRestProgressPct(0);
        currentCountRef.current = 0;
        currentSetRef.current++;
        setExerciseCount(0);
        setProgressPct(0);
        setCurrentSet(currentSetRef.current);
        hasGoneDown.current = false;
        armsUp.current = false;
        legsApart.current = false;
        hasCompletedJump.current = false;
        squatStartTime.current = null;
        jumpStartTime.current = null;
        isDetectingRef.current = true;
        speakMessage(language === 'mr'
          ? `सेट ${currentSetRef.current} तयार`
          : `Set ${currentSetRef.current} ready`);
          playWhistle();
        setStatus(t.ready);
      }
    }, 1000);
  }, [language, speakMessage, speakNumber, playWhistle, t.ready]);

  const handleRepComplete = useCallback(() => {
    currentCountRef.current++;
    totalRepsCompleted.current++;
    const count = currentCountRef.current;
    const rps = repsPerSetRef.current;
    const ts = totalSetsRef.current;

    setExerciseCount(count);
    setProgressPct((count / rps) * 100);
    speakNumber(count);
    if (navigator.vibrate) navigator.vibrate(100);
    playBeep();

    const remaining = rps - count;
    if (remaining > 0) {
      setStatus(language === 'mr' ? `${remaining} अजून!` : `${remaining} more!`);
    }

    if (count >= rps) {
      isDetectingRef.current = false;
      if (currentSetRef.current >= ts) {
        const total = totalRepsCompleted.current;
        setTotalRepsLabel(`${total} ${exerciseName} ${t.done}`);
        updateUserStatsBackground(total);
        playClap();
        if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);
        if (videoRef.current?.srcObject) {
          videoRef.current.srcObject.getTracks().forEach(track => track.stop());
        }
        setTimeout(() => setShowCompletion(true), 500);
      } else {
        setTimeout(() => startRestTimer(), 1000);
      }
    }
  }, [language, exerciseName, t.done, speakNumber, playBeep, playClap,
    startRestTimer, updateUserStatsBackground]);

  const drawSkeleton = useCallback((ctx, landmarks) => {
    const connections = [
      [11,12],[11,13],[13,15],[12,14],[14,16],
      [11,23],[12,24],[23,24],
      [23,25],[25,27],[24,26],[26,28]
    ];
    ctx.strokeStyle = '#10b981';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    connections.forEach(([s, e]) => {
      const sp = landmarks[s], ep = landmarks[e];
      if (sp?.visibility > 0.5 && ep?.visibility > 0.5) {
        ctx.beginPath();
        ctx.moveTo(sp.x * ctx.canvas.width, sp.y * ctx.canvas.height);
        ctx.lineTo(ep.x * ctx.canvas.width, ep.y * ctx.canvas.height);
        ctx.stroke();
      }
    });
    ctx.fillStyle = '#3b82f6';
    [11,12,13,14,15,16,23,24,25,26,27,28].forEach(i => {
      const lm = landmarks[i];
      if (lm?.visibility > 0.5) {
        ctx.beginPath();
        ctx.arc(lm.x * ctx.canvas.width, lm.y * ctx.canvas.height, 5, 0, 2 * Math.PI);
        ctx.fill();
      }
    });
  }, []);

  const onResults = useCallback((results) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext('2d');
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    ctx.clearRect(0, 0, w, h);
    if (!results.poseLandmarks?.length) {
      if (isDetectingRef.current) {
        setStatus(language === 'mr' ? 'पूर्ण शरीर दाखवा' : 'Show your full body');
      }
      smoothScoreRef.current *= 0.8;
      if (barFillRef.current) {
        barFillRef.current.style.height = Math.round(smoothScoreRef.current * 100) + '%';
      }
      return;
    }
    drawSkeleton(ctx, results.poseLandmarks);

    const scoreResult = exerciseType === 'squats'
      ? calculateSquatScore(results.poseLandmarks, standingKneeAngle)
      : exerciseType === 'jumpingJacks'
        ? calculateJJScore(results.poseLandmarks)
        : null;
    if (scoreResult) {
      const target = scoreResult.visOK ? scoreResult.score : 0;
      smoothScoreRef.current = smoothScoreRef.current * 0.5 + target * 0.5;
      if (barFillRef.current) {
        barFillRef.current.style.height = Math.round(smoothScoreRef.current * 100) + '%';
      }
    }

    if (!isDetectingRef.current || isRestingRef.current) return;
    const params = {
      landmarks: results.poseLandmarks,
      hasGoneDown, squatStartTime, standingKneeAngle, maxSquatDepth,
      armsUp, legsApart, hasCompletedJump, jumpStartTime,
      language, onRepComplete: handleRepComplete, setStatus
    };
    if (exerciseType === 'squats') detectSquats(params);
    else if (exerciseType === 'jumpingJacks') detectJumpingJacks(params);
  }, [drawSkeleton, handleRepComplete, exerciseType, language]);

  const startCamera = useCallback(async () => {
    try {
      setStatus(t.loading);
      const pose = new window.Pose({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`
      });
      pose.setOptions({
        modelComplexity: 0,
        smoothLandmarks: true,
        enableSegmentation: false,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });
      pose.onResults(onResults);
      poseRef.current = pose;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' }
      });
      videoRef.current.srcObject = stream;

      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('timeout')), 15000);
        const done = () => { clearTimeout(timeout); resolve(); };
        videoRef.current.addEventListener('loadeddata', done, { once: true });
        videoRef.current.addEventListener('canplay', done, { once: true });
        if (videoRef.current.readyState >= 2) done();
      });

      const camera = new window.Camera(videoRef.current, {
        onFrame: async () => {
          if (poseRef.current && !isRestingRef.current) {
            try { await poseRef.current.send({ image: videoRef.current }); }
            catch (e) {}
          }
        },
        width: 640,
        height: 480
      });
      cameraRef.current = camera;
      await camera.start();
      setCameraStarted(true);
      setStatus(t.ready);
    } catch (e) {
      setStatus(language === 'mr' ? 'कॅमेरा त्रुटी' : 'Camera error');
      setShowWhatsApp(true);
    }
  }, [onResults, t.loading, t.ready, language]);

  useEffect(() => {
    currentCountRef.current = 0;
    currentSetRef.current = 1;
    totalRepsCompleted.current = 0;
    isDetectingRef.current = true;
    isRestingRef.current = false;

    const outlineTimer = setTimeout(() => setShowOutline(false), 8000);

    let retries = 0;
    const tryStart = async () => {
      if (typeof window.Pose === 'undefined' || typeof window.Camera === 'undefined') {
        if (retries < 15) {
          retries++;
          setStatus(language === 'mr'
            ? `कॅमेरा लोड होत आहे... (${retries})`
            : `Loading... (${retries})`);
          setTimeout(tryStart, 2000);
        } else {
          setStatus(language === 'mr' ? 'लोड अयशस्वी' : 'Load failed');
          setShowWhatsApp(true);
        }
        return;
      }
      await startCamera();
    };
    setTimeout(tryStart, 500);

    return () => {
      clearTimeout(outlineTimer);
      cleanup();
    };
  }, [language, startCamera, cleanup]);

  useEffect(() => {
    if (cameraStarted) return;
    timeoutRef.current = setTimeout(() => {
      setCameraStarted(prev => {
        if (!prev) setShowWhatsApp(true);
        return prev;
      });
    }, 30000);
    return () => clearTimeout(timeoutRef.current);
  }, [cameraStarted]);

  useEffect(() => {
    if (showCompletion) {
      cleanup();
      completionTimerRef.current = setTimeout(() => {
        setShowCompletion(false);
        cleanup();
        if (onComplete) onComplete();
      }, 10000);
    }
    return () => clearTimeout(completionTimerRef.current);
  }, [showCompletion, cleanup, onComplete]);

  const handleWhatsApp = () => {
    const msg = language === 'mr'
      ? 'नमस्कार, माझा कॅमेरा उघडला नाही.'
      : 'Hi, my camera did not open in Spocademy.';
    window.open(`https://wa.me/919359246193?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleConfirmClose = () => {
    cleanup();
    if (onBack) onBack();
  };

  return (
    <div className="ec-container">
      <div className="ec-header">
        <div style={{ width: 36 }} />
        <div className="ec-title">{exerciseName}</div>
        <button className="ec-close-btn" onClick={() => setShowConfirmClose(true)}>✕</button>
      </div>

      <div className="ec-counter-row">
        {isResting ? (
          <>
            <div className="ec-rest-label">{t.rest}</div>
            <div className="ec-rest-count">{restTime}</div>
          </>
        ) : (
          <>
            <div className="ec-rep-count">{exerciseCount}/{repsPerSet}</div>
            <div className="ec-set-count">
              <div className="ec-set-label">{t.set}</div>
              <div className="ec-set-number">{currentSet}/{totalSets}</div>
            </div>
          </>
        )}
      </div>

      <div className="ec-camera-wrap">
        <video ref={videoRef} autoPlay muted playsInline className="ec-video" />
        <canvas ref={canvasRef} className="ec-canvas" />
        {showOutline && (
          <div className="ec-outline-wrap">
            <img src="/person-standing.svg" alt="position guide" className="ec-outline-img" />
          </div>
        )}
        {showWhatsApp && (
          <button className="ec-whatsapp-btn" onClick={handleWhatsApp}>
            <WhatsAppIcon />
            {language === 'mr' ? 'मदत हवी आहे?' : 'Need help?'}
          </button>
        )}
        <div className="ec-bar-wrap"><div className="ec-bar-fill" ref={barFillRef} /></div>
      </div>

      <div className="ec-progress-wrap">
        <div className="ec-progress-bar">
          <div
            className={isResting ? 'ec-progress-fill rest' : 'ec-progress-fill'}
            style={{ width: `${isResting ? restProgressPct : progressPct}%` }}
          />
        </div>
      </div>

      <div className="ec-status">{status}</div>

      {showConfirmClose && (
        <div className="ec-overlay">
          <div className="ec-confirm-box">
            <p>{t.confirmClose}</p>
            <div className="ec-confirm-btns">
              <button className="ec-confirm-no" onClick={() => setShowConfirmClose(false)}>
                {t.no}
              </button>
              <button className="ec-confirm-yes" onClick={handleConfirmClose}>
                {t.yes}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompletion && (
        <div className="ec-overlay">
          <div className="ec-completion-box">
            <div className="ec-completion-emoji">💪</div>
            <div className="ec-completion-text">{totalRepsLabel}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedCamera;